import React from "react";
import { Button } from "react-bootstrap";
import { useFormikContext } from "formik";
import {
    UnsavedChangesModal,
    useUnsavedChangesGuard,
    useUnsavedChangesGuardContext,
    useFormikUnsavedGuardConfig,
    shouldDelegateCancelToSharedGuard,
    type LeaveSaveOutcome,
    type UnsavedChangesGuardContextValue,
} from "@/features/unsavedChanges";
import { LoadingSpinner } from "../common/Loading/LoadingSpinner";

/** Right-aligned Save/Cancel group (full-width row). */
export function EditFormActions({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const classes = `d-flex flex-wrap gap-2 justify-content-end w-100 mt-4 ${className}`.trim();
  return <div className={classes}>{children}</div>;
}

/** Footer with optional left admin actions and right-aligned Save/Cancel. */
export function EditFormSplitFooter({
  start,
  children,
  className = "my-4",
}: {
  start?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const rowClass = `d-flex flex-wrap gap-2 justify-content-between align-items-center w-100 ${className}`.trim();
  return (
    <div className={rowClass}>
      {start ?? null}
      <div className="d-flex flex-wrap gap-2 justify-content-end ms-auto">{children}</div>
    </div>
  );
}

export interface EditFormSaveCancelButtonsProps {
  isSubmitting: boolean;
  onCancel: () => void;
  saveLabel?: string;
  /** Disables Save only; does not affect the unsaved-changes Cancel guard. */
  disabled?: boolean;
  /** When true (e.g. deleted record), skips the unsaved-changes Cancel guard. */
  readOnlyForm?: boolean;
  /** default true — set false to skip unsaved-changes Cancel guard */
  enableUnsavedChangesGuard?: boolean;
  /** When true with {@link newRecordLabel}, uses discard-new-record modal copy. */
  isNewRecord?: boolean;
  newRecordLabel?: string;
  unsavedChangesTitle?: string;
  unsavedChangesMessage?: string;
  unsavedChangesCancelLabel?: string;
  unsavedChangesContinueWithoutSavingLabel?: string;
  /** When true on edit pages, enables Save and continue. Ignored when {@link isNewRecord}. */
  enableSaveBeforeLeave?: boolean;
  /** Page-level ref set during save-before-leave submit; required with {@link saveOutcomeRef} when enabling save-before-leave. */
  leaveAfterSaveRef?: React.MutableRefObject<boolean>;
  saveOutcomeRef?: React.MutableRefObject<LeaveSaveOutcome>;
  /** Optional override; defaults to hook wired from Formik context and refs above. */
  onSaveBeforeLeave?: () => Promise<boolean>;
}

type EditFormSaveCancelButtonsSharedProps = Pick<
  EditFormSaveCancelButtonsProps,
  "isSubmitting" | "onCancel" | "saveLabel" | "disabled"
> & {
  sharedGuard: UnsavedChangesGuardContextValue;
};

function EditFormSaveCancelButtonsShared({
  isSubmitting,
  onCancel,
  saveLabel = "Save",
  disabled = false,
  sharedGuard,
}: EditFormSaveCancelButtonsSharedProps) {
  const { submitForm } = useFormikContext();

  return (
    <>
      <Button
        variant="primary"
        type="button"
        disabled={disabled || isSubmitting}
        onClick={() => void submitForm()}
      >
        {isSubmitting ? <LoadingSpinner text="Saving…" /> : saveLabel}
      </Button>
      <Button
        variant="outline-secondary"
        type="button"
        onClick={() => sharedGuard.requestProceed(onCancel)}
      >
        Cancel
      </Button>
    </>
  );
}

function EditFormSaveCancelButtonsLocal(props: EditFormSaveCancelButtonsProps) {
  const {
    isSubmitting,
    onCancel,
    saveLabel = "Save",
    disabled = false,
    readOnlyForm = false,
    enableUnsavedChangesGuard = true,
    isNewRecord = false,
    newRecordLabel,
    unsavedChangesTitle,
    unsavedChangesMessage,
    unsavedChangesCancelLabel,
    unsavedChangesContinueWithoutSavingLabel,
    enableSaveBeforeLeave = false,
    leaveAfterSaveRef,
    saveOutcomeRef,
    onSaveBeforeLeave: onSaveBeforeLeaveProp,
  } = props;

  const { submitForm } = useFormikContext();

  const guardConfig = useFormikUnsavedGuardConfig({
    readOnlyForm,
    enableUnsavedChangesGuard,
    isNewRecord,
    newRecordLabel,
    unsavedChangesTitle,
    unsavedChangesMessage,
    unsavedChangesCancelLabel,
    unsavedChangesContinueWithoutSavingLabel,
    enableSaveBeforeLeave,
    leaveAfterSaveRef,
    saveOutcomeRef,
    onSaveBeforeLeave: onSaveBeforeLeaveProp,
  });

  const guard = useUnsavedChangesGuard(guardConfig);

  return (
    <>
      <Button
        variant="primary"
        type="button"
        disabled={disabled || isSubmitting}
        onClick={() => void submitForm()}
      >
        {isSubmitting ? <LoadingSpinner text="Saving…" /> : saveLabel}
      </Button>
      <Button
        variant="outline-secondary"
        type="button"
        onClick={() => guard.requestProceed(onCancel)}
      >
        Cancel
      </Button>
      <UnsavedChangesModal {...guard.modalProps} />
    </>
  );
}

export function EditFormSaveCancelButtons(props: EditFormSaveCancelButtonsProps) {
  const sharedGuard = useUnsavedChangesGuardContext();

  if (shouldDelegateCancelToSharedGuard(sharedGuard !== null)) {
    return (
      <EditFormSaveCancelButtonsShared
        isSubmitting={props.isSubmitting}
        onCancel={props.onCancel}
        saveLabel={props.saveLabel}
        disabled={props.disabled}
        sharedGuard={sharedGuard!}
      />
    );
  }

  return <EditFormSaveCancelButtonsLocal {...props} />;
}

